import React, { useEffect, useState } from 'react';

import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { InvitedUser } from '~/services/CorporateEventsService';

interface InvitedUserDialogProps {
  open: boolean;
  invitedUser?: InvitedUser | null;
  onClose: () => void;
  onSave: (invitedUser: InvitedUser) => void;
  isSaving?: boolean;
}

export default function InvitedUserDialog({
  open,
  invitedUser,
  onClose,
  onSave,
  isSaving = false,
}: InvitedUserDialogProps) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    department: '',
    role: '',
    notes: '',
    hasAnalyticsAccess: false,
  });

  const isEditMode = !!invitedUser;

  useEffect(() => {
    if (open) {
      if (invitedUser) {
        setFormData({
          fullName: invitedUser.fullName || '',
          email: invitedUser.email || '',
          department: invitedUser.department || '',
          role: invitedUser.role || '',
          notes: invitedUser.notes || '',
          hasAnalyticsAccess: invitedUser.hasAnalyticsAccess || false,
        });
      } else {
        setFormData({
          fullName: '',
          email: '',
          department: '',
          role: '',
          notes: '',
          hasAnalyticsAccess: false,
        });
      }
    }
  }, [open, invitedUser]);

  const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (field === 'hasAnalyticsAccess') {
      setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleSave = () => {
    if (!formData.email.trim()) {
      return;
    }

    const userData: InvitedUser = {
      ...(invitedUser?.id && { id: invitedUser.id }),
      ...(invitedUser?.eventId && { eventId: invitedUser.eventId }),
      fullName: formData.fullName.trim() || undefined,
      email: formData.email.trim(),
      department: formData.department.trim() || undefined,
      role: formData.role.trim() || undefined,
      notes: formData.notes.trim() || undefined,
      hasAnalyticsAccess: formData.hasAnalyticsAccess,
    };

    onSave(userData);
  };

  const handleClose = () => {
    setFormData({
      fullName: '',
      email: '',
      department: '',
      role: '',
      notes: '',
      hasAnalyticsAccess: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Invited User' : 'Add Invited User'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} mt={1}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Full Name" value={formData.fullName} onChange={handleChange('fullName')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={handleChange('department')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth label="Role" value={formData.role} onChange={handleChange('role')} />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleChange('notes')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hasAnalyticsAccess}
                    onChange={handleChange('hasAnalyticsAccess')}
                    color="primary"
                  />
                }
                label="Has Analytics Access"
              />
              <FormHelperText>
                Enables the user to view statistics and export registration results to a file.
              </FormHelperText>
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSaving || !formData.email.trim()}
          startIcon={isSaving ? <CircularProgress size={20} /> : undefined}
        >
          {isSaving ? (isEditMode ? 'Updating...' : 'Adding...') : isEditMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
